import { useEffect, useState } from "react";
import axios from "axios";
import { Clock, Check } from "lucide-react";
import { TIMEZONES, timezoneLabel } from "../lib/timezones";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Captures the user's timezone from the browser the first time they're signed
 * in (covers EVERY signup path in one place) so notification times are right
 * from day one. For a brand-new user we also show a quick onboarding
 * confirmation — "You're in <zone> — correct?" — so they can confirm or change
 * it in one tap. Respects an existing saved choice; runs at most once per
 * browser (guarded by iw_user_tz) and confirms once (guarded by iw_tz_confirmed).
 */
export default function TimezoneCapture({ user }) {
  const [confirmZone, setConfirmZone] = useState(null); // auto-detected zone awaiting confirmation
  const [picking, setPicking] = useState(false);
  const [pick, setPick] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user || user.isDemo) return undefined;
    const uid = user.id || user.email;
    if (!uid) return undefined;

    let cached = null;
    try { cached = localStorage.getItem("iw_user_tz"); } catch { /* storage off */ }
    if (cached) return undefined; // already captured on this browser

    let detected = null;
    try { detected = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { /* unsupported */ }
    if (!detected) return undefined;

    let confirmed = null;
    try { confirmed = localStorage.getItem("iw_tz_confirmed"); } catch { /* storage off */ }

    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/profile/${uid}`);
        if (cancelled) return;
        const existing = r.data?.timezone;
        if (existing) {
          try { localStorage.setItem("iw_user_tz", existing); } catch { /* storage off */ }
          return;
        }
        // New user, no saved zone → set the detected one and offer a quick confirm.
        await axios.put(`${API}/api/profile/update`, { user_id: uid, timezone: detected });
        if (cancelled) return;
        try { localStorage.setItem("iw_user_tz", detected); } catch { /* storage off */ }
        if (!confirmed) { setPick(detected); setConfirmZone(detected); }
      } catch { /* non-blocking: notifications still fall back to Eastern */ }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const dismiss = () => {
    try { localStorage.setItem("iw_tz_confirmed", "1"); } catch { /* storage off */ }
    setConfirmZone(null);
    setPicking(false);
  };

  const savePick = async () => {
    const uid = user && (user.id || user.email);
    if (!uid) return;
    setSaving(true);
    try {
      await axios.put(`${API}/api/profile/update`, { user_id: uid, timezone: pick || "" });
      try {
        if (pick) localStorage.setItem("iw_user_tz", pick);
        else localStorage.removeItem("iw_user_tz");
      } catch { /* storage off */ }
    } catch { /* non-blocking */ }
    setSaving(false);
    dismiss();
  };

  if (!confirmZone) return null;

  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 bottom-20 sm:bottom-6 z-[9998] w-[calc(100%-2rem)] max-w-sm"
      data-testid="tz-confirm"
    >
      <div className="rounded-2xl bg-slate-900/98 border border-sky-400/30 shadow-2xl p-4 backdrop-blur-sm">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-xl bg-sky-500/15 flex items-center justify-center shrink-0">
            <Clock size={18} className="text-sky-300" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white font-fredoka font-bold text-sm">Is this your time zone?</p>
            <p className="text-white/55 text-xs mt-0.5">
              We set notifications to <span className="text-sky-200 font-bold">{timezoneLabel(confirmZone)}</span>. Correct?
            </p>
          </div>
        </div>

        {!picking ? (
          <div className="flex gap-2 mt-3">
            <button
              onClick={dismiss}
              className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-sm font-bold bg-emerald-500/80 hover:bg-emerald-500 text-white"
              data-testid="tz-confirm-yes"
            >
              <Check size={15} /> Yes, that's right
            </button>
            <button
              onClick={() => setPicking(true)}
              className="px-3 py-2 rounded-xl text-sm font-bold bg-white/10 hover:bg-white/20 text-white/80 border border-white/15"
              data-testid="tz-confirm-change"
            >
              Change
            </button>
          </div>
        ) : (
          <div className="mt-3 space-y-2">
            <select
              value={pick}
              onChange={(e) => setPick(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl bg-white/[0.06] border border-white/12 text-white text-sm focus:border-sky-400/50 focus:outline-none"
              data-testid="tz-confirm-select"
            >
              <option value="" className="bg-slate-800">Eastern Time (ET) — default</option>
              {TIMEZONES.map((z) => (
                <option key={z.value} value={z.value} className="bg-slate-800">{z.label}</option>
              ))}
            </select>
            <button
              onClick={savePick}
              disabled={saving}
              className="w-full inline-flex items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-sm font-bold bg-sky-500/80 hover:bg-sky-500 text-white disabled:opacity-50"
              data-testid="tz-confirm-save"
            >
              <Check size={15} /> {saving ? "Saving…" : "Save time zone"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
